if (!customElements.get("text-slider")) {
  customElements.define(
    "text-slider",
    class TextSlider extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.slideshowSelector = this.dataset.slideshowSelector;
        this.carousel = this.slideshowSelector
          ? this.querySelector(this.slideshowSelector)
          : this.querySelector(".media-icon-items-grid");
        this.initialize();
      }

      disconnectedCallback() {
        if (this.autoHeight) {
          window.removeEventListener("resize", this.handleResize);
        }
      }

      initialize() {
        const autoplayEnabled = this.getAttribute("data-autoplay") === "true";
        const autoPlaySpeed = parseInt(this.getAttribute("data-speed"), 10) * 1000;
        this.animationType = this.getAttribute("data-animation-type") ?? "slide";
        this.autoPlayEnabled = autoplayEnabled && autoPlaySpeed > 0;
        this.autoHeight = this.getAttribute("data-auto-height") === "true";

        const cellSelector = this.dataset.cellSelector ?? ".media-icon-item";

        if (this.dataset.setDisplay === "true") {
          this.carousel.style.display = "block";
        }

        this.flickity = new Flickity(this.carousel, {
          autoPlay: autoplayEnabled ? autoPlaySpeed : false,
          cellAlign: "center",
          groupCells: false,
          contain: true,
          resize: true,
          draggable: true,
          prevNextButtons: false,
          cellSelector: cellSelector,
          initialIndex: 0,
          pageDots: false,
          wrapAround: true,
          accessibility: false,
          watchCSS: true,
        });

        if (this.autoHeight) {
          window.addEventListener("resize", this.handleResize);
        }

        requestAnimationFrame(() => {
          this.dispatchEvent(
            new CustomEvent("text-slider:ready", {
              bubbles: true,
              detail: {
                id: this.id,
                element: this,
                flickity: this.flickity,
              },
            }),
          );
        });
      }

      handleResize = debounce(() => {
        this.recalculateHeight();
      }, 200);

      recalculateHeight() {
        if (!this.flickity) {
          return;
        }

        const slides = this.flickity.getCellElements();
        const maxHeight = slides.reduce((tallest, slide) => {
          return Math.max(tallest, slide.offsetHeight);
        }, 0);

        const viewport = this.querySelector(".flickity-viewport");
        if (viewport) {
          viewport.style.height = `${maxHeight}px`;
        }
      }
    },
  );
}

if (!customElements.get("text-slider-navigation")) {
  customElements.define(
    "text-slider-navigation",
    class TextSliderNavigation extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.targetId = this.getAttribute("for");
        this.buttonPrev = this.querySelector(".js-prev");
        this.buttonNext = this.querySelector(".js-next");

        this.onPrevClick = (event) => {
          event.preventDefault();
          this.flickity?.previous();
        };

        this.onNextClick = (event) => {
          event.preventDefault();
          this.flickity?.next();
        };

        this.onSliderReady = (event) => {
          if (event.detail.id !== this.targetId) {
            return;
          }

          this.attachToSlider(event.detail.element, event.detail.flickity);
        };

        this.buttonPrev?.addEventListener("click", this.onPrevClick);
        this.buttonNext?.addEventListener("click", this.onNextClick);
        document.addEventListener("text-slider:ready", this.onSliderReady);

        this.attachToExistingSlider();
      }

      disconnectedCallback() {
        this.buttonPrev?.removeEventListener("click", this.onPrevClick);
        this.buttonNext?.removeEventListener("click", this.onNextClick);
        document.removeEventListener("text-slider:ready", this.onSliderReady);
        this.detachFromSlider();
      }

      attachToExistingSlider() {
        const carouselComponent = document.querySelector(`#${this.targetId}`);
        if (!carouselComponent?.flickity) {
          return;
        }

        this.attachToSlider(carouselComponent, carouselComponent.flickity);
      }

      attachToSlider(carouselComponent, flickity) {
        if (!carouselComponent || !flickity || this.flickity === flickity) {
          return;
        }

        this.detachFromSlider();

        this.carouselComponent = carouselComponent;
        this.flickity = flickity;

        this.syncButtons = () => this.updateButtons();
        this.flickity.on("select", this.syncButtons);
        this.flickity.on("settle", this.syncButtons);
        this.updateButtons();
      }

      updateButtons() {
        if (!this.flickity || !this.buttonPrev || !this.buttonNext) {
          return;
        }

        if (this.flickity.options.wrapAround) {
          return;
        }

        this.enableButton(this.buttonPrev);
        this.enableButton(this.buttonNext);

        const slides = this.flickity.slides;
        if (!slides || slides.length === 0) {
          return;
        }

        const current = slides[this.flickity.selectedIndex];
        const first = slides[0];
        const last = slides[slides.length - 1];

        if (current.target === first.target) {
          this.disableButton(this.buttonPrev);
        }
        if (current.target === last.target) {
          this.disableButton(this.buttonNext);
        }
      }

      detachFromSlider() {
        if (!this.flickity) {
          return;
        }

        this.flickity = null;
        this.carouselComponent = null;
      }

      enableButton(button) {
        if (!button) return;
        button.classList.remove("pill-nav-item-disabled");
        button.classList.remove("focus-none");
        button.setAttribute("aria-disabled", "false");
      }

      disableButton(button) {
        if (!button) return;
        button.classList.add("pill-nav-item-disabled");
        button.classList.add("focus-none");
        button.setAttribute("aria-disabled", "true");
      }
    },
  );
}
